import React, { useContext } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@com/core/common/translations/i18n';
import { shoppingUrl } from '@com/core/common/utils/client';
import { breakpoints } from '@com/core/common/styles/baseStyles';
import { misc } from '@com/core/noon/styles/noonTheme';
import { MARKETPLACE_CONFIG } from '@com/core/common/utils/config';
import { AppContext } from '../../common/contexts/appContext';

const EmptyStateContainer = styled.div`
    /* the 60px is from the footer margin-top */
    /* if header title is the min 20px needed */
    min-height: calc(100vh - ${misc.siteHeaderHeightMobile} - 60px - 20px);
    @media ${breakpoints.minTablet} {
        min-height: calc(100vh - ${misc.siteHeaderHeightDesktop} - 60px - 20px);
    }

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 15px;
    &.container-cart {
        height: 100%;
        padding: 100px;
    }
`;

const Image = styled.img`
    width: auto;
    height: 30vh;
    margin-bottom: 30px;
    display: block;
`;

const Heading = styled.p`
    margin-bottom: 4px;
    font-family: sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 2rem;
    line-height: 24px;
`;

const SubCopy = styled.p`
    color: ${({ theme: { colors } }): string => colors.body};
    font-size: 1.166666rem;
`;

const Cta = styled.button`
    margin-top: 20px;
    width: 250px;
    color: ${({ theme: { colors } }): string => colors.bgPrimary};
    background-color: ${({ theme: { colors } }): string => colors.brandSecondary};
    border: solid 1px ${({ theme: { colors } }): string => colors.brandSecondary};
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    padding: 12px 20px;
    font-size: 1rem;
    border-radius: 4px;
    cursor: pointer;
`;

type TProps = {
    type?: string | number;
    heading?: string;
};

type TContentType = {
    image: string;
    heading: string;
    subCopy: string;
    ctaText: string;
    ctaUrl: string;
    containerClassName: string;
};

const EmptyState = ({ heading, type }: TProps): JSX.Element => {
    const { locale } = useContext(AppContext);
    const { seoLocale } = locale;
    const { t } = useTranslation('common');
    let content: TContentType = {
        // default
        image: MARKETPLACE_CONFIG.assets.images.error5xx,
        heading: heading || t('_emptyState_5xxHeadingWrong'),
        subCopy: t('_emptyState_5xxSubCopy'),
        ctaText: t('return-to-homepage'),
        ctaUrl: `/${seoLocale}/`,
        containerClassName: 'container',
    };

    switch (type) {
        case 'cart':
            content = {
                image: MARKETPLACE_CONFIG.assets.images.cartEmpty, // Similaraly we should follow this
                heading: t('_emptyState_cartHeading'),
                subCopy: t('_emptyState_cartSubCopy'),
                ctaText: t('start-shopping'),
                ctaUrl: `/${seoLocale}/`,
                containerClassName: 'container container-cart',
            };
            break;
        case 'product':
            content = {
                image: MARKETPLACE_CONFIG.assets.images.emptySearch,
                heading: t('_emptyState_productHeading'),
                subCopy: t('_emptyState_productSubCopy'),
                ctaText: t('continue-shopping'),
                ctaUrl: `/${seoLocale}${shoppingUrl.get()}`,
                containerClassName: 'container',
            };
            break;
        case '429':
        case 429:
            content = {
                image: MARKETPLACE_CONFIG.assets.images.error429,
                heading: heading || t('_emptyState_404HeadingWrong'),
                subCopy: t('_emptyState_404SubCopy'),
                ctaText: t('back-to-homepage'),
                ctaUrl: `/${seoLocale}/`,
                containerClassName: 'container',
            };
            break;
        case '404':
        case 404:
        default:
            content = {
                image: MARKETPLACE_CONFIG.assets.images.error404,
                heading: heading || t('_emptyState_404HeadingWrong'),
                subCopy: t('_emptyState_404SubCopy'),
                ctaText: t('back-to-homepage'),
                ctaUrl: `/${seoLocale}/`,
                containerClassName: 'container',
            };
    }

    const navigateAway = (): void => {
        window.location.href = content.ctaUrl;
    };

    return (
        <EmptyStateContainer>
            <Image src={content.image} alt={content.heading} />
            <Heading>{content.heading}</Heading>
            {content.subCopy && <SubCopy>{content.subCopy}</SubCopy>}
            {content.ctaText && (
                <Cta type="button" onClick={navigateAway} aria-label={content.ctaText}>
                    {content.ctaText}
                </Cta>
            )}
        </EmptyStateContainer>
    );
};

export default EmptyState;
